import React, { Suspense } from 'react';
import { Outlet, type RouteObject } from 'react-router';
import { HasPermission } from '@/components/Permission/HasPermission';
import { LotteryAccessGuard } from '@/components/Permission/LotteryAccessGuard';
import LoadingCompanyDashboard from '@/components/UI/loading/LoadingCompanyDashboard';
import Unauthorized from '@/components/Unauthorized';
import { OSProjectTemplateDetailsShell, OSProjectTemplateTDMImportShell } from '@/features/ProjectTemplates/Shells/OS';
import type { HasPermissionFn } from '@/hooks/usePermission/index.tsx';
import GovernmentIntakeAdminListView from '@/modules/governmentIntake/pages/admin-list/AdminListView.tsx';
import Administration from '@/pages/Administration/index.tsx';
import PageNotFound from '@/pages/PageNotFound';
import Report from '@/pages/Report';
import Sales from '@/pages/Report/Sales';
import Settings from '@/pages/Settings/index.tsx';
import AdministrationRoute from '../AppRoute/AdministrationRoute/index.tsx';
import { LotteryRoute } from '../AppRoute/LotteryRoute';
import SettingsRoute from '../AppRoute/SettingsRoute/index.tsx';

const Clients = React.lazy(() => import('@/pages/SageAdmin/Clients'));
const ClientDetails = React.lazy(() => import('@/pages/SageAdmin/Clients/Details'));
const OSProjectTemplates = React.lazy(() => import('@/pages/OS/ProjectTemplates'));

/**
 * OSRoute - Operating System Routes (SageAdmin Only)
 *
 * These routes are only accessible to users with sageAdmin account type.
 * Non-admin users attempting to access these routes will be redirected
 * by the RequireAdmin guard.
 */
export default function OSRoute(hasPermission: HasPermissionFn) {
  const routes = [
    {
      path: 'clients',
      children: [
        {
          index: true,
          element: (
            <HasPermission of='LIST_CLIENTS' fallback={<Unauthorized />}>
              <Suspense fallback={<LoadingCompanyDashboard />}>
                <Clients />
              </Suspense>
            </HasPermission>
          ),
        },
        {
          path: ':clientId',
          element: (
            <HasPermission of='READ_CLIENT' fallback={<Unauthorized />}>
              <Suspense fallback={<LoadingCompanyDashboard />}>
                <ClientDetails />
              </Suspense>
            </HasPermission>
          ),
        },
      ],
    },
    {
      path: 'administration',
      element: <Administration />,
      children: AdministrationRoute(hasPermission),
    },
    {
      path: 'settings',
      element: <Settings />,
      children: SettingsRoute(),
    },
    {
      path: 'sales-marketting',
      children: [
        {
          index: true,
          element: <Report />,
        },
        {
          path: 'sales',
          element: <Sales />,
        },
        {
          path: 'government-intake',
          element: <GovernmentIntakeAdminListView />,
        },
      ],
    },
    {
      path: 'project-templates',
      children: [
        {
          index: true,
          element: (
            <HasPermission of='LIST_PROJECT_TEMPLATES' fallback={<Unauthorized />}>
              <Suspense fallback={<LoadingCompanyDashboard />}>
                <OSProjectTemplates />
              </Suspense>
            </HasPermission>
          ),
        },
        {
          path: ':templateId',
          element: (
            <HasPermission of='READ_PROJECT_TEMPLATE' fallback={<Unauthorized />}>
              <OSProjectTemplateDetailsShell />
            </HasPermission>
          ),
        },
        {
          path: ':templateId/import',
          element: (
            <HasPermission of='UPDATE_PROJECT_TEMPLATE' fallback={<Unauthorized />}>
              <OSProjectTemplateTDMImportShell />
            </HasPermission>
          ),
        },
      ],
    },
    {
      path: 'lottery',
      element: (
        <LotteryAccessGuard>
          <Outlet />
        </LotteryAccessGuard>
      ),
      children: LotteryRoute(),
    },
    {
      path: '*',
      element: <PageNotFound />,
    },
  ] as RouteObject[];

  return routes;
}
